"use client";

import React from "react";
import { usePathname } from "next/navigation";
import { apiPath } from "@/lib/apiPath";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import Checkbox from "@/components/ui/Checkbox";
import SafeHtml from "@/components/ui/SafeHtml";

type CF7InvalidField = {
    field: string;
    message: string;
};

type CF7Response = {
    status: string;
    message?: string;
    invalid_fields?: CF7InvalidField[];
};

type Copy = {
    name_label?: string;
    email_label?: string;
    indicates_required?: string;
    terms_legal?: string;
    newsletter_legal?: string;
    submit?: string;
    honeypot?: string;
};

type Props = {
    formId: string;
    langCode?: string;
    copy?: Copy;
};

function getCF7Locale(langCode: string) {
    if (langCode === "pt") return "pt_PT";
    if (langCode === "en") return "en_US";
    return langCode || "pt_PT";
}

export default function FooterContactFormClient({
    formId,
    langCode = "pt",
    copy,
}: Props) {
    const [name, setName] = React.useState("");
    const [email, setEmail] = React.useState("");
    const [website, setWebsite] = React.useState("");
    const [terms, setTerms] = React.useState(false);
    const [newsletter, setNewsletter] = React.useState(false);
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [topMessage, setTopMessage] = React.useState<string | null>(null);
    const [topMessageType, setTopMessageType] =
        React.useState<"success" | "error" | null>(null);
    const [nameError, setNameError] = React.useState<string | null>(null);
    const [emailError, setEmailError] = React.useState<string | null>(null);
    const [termsError, setTermsError] = React.useState<string | null>(null);

    const nameRef = React.useRef<HTMLInputElement | null>(null);
    const emailRef = React.useRef<HTMLInputElement | null>(null);
    const formRef = React.useRef<HTMLFormElement | null>(null);
    const formTokenRef = React.useRef<string | null>(null);
    const didMountRef = React.useRef(false);

    const pathname = usePathname();

    const fetchFormToken = React.useCallback(async () => {
        try {
            const res = await fetch(apiPath("/api/contact-token"), { cache: "no-store" });

            if (!res.ok) {
                formTokenRef.current = null;
                return;
            }

            const data = (await res.json()) as { token?: string };
            formTokenRef.current = data.token || null;
        } catch {
            formTokenRef.current = null;
        }
    }, []);

    const resetForm = React.useCallback(() => {
        setName("");
        setEmail("");
        setWebsite("");
        setTerms(false);
        setNewsletter(false);
        setIsSubmitting(false);
        setNameError(null);
        setEmailError(null);
        setTermsError(null);

        if (didMountRef.current) {
            formRef.current?.reset();
        }

        void fetchFormToken();
    }, [fetchFormToken]);

    React.useEffect(() => {
        didMountRef.current = true;
        void fetchFormToken();
    }, [formId, langCode, fetchFormToken]);

    React.useEffect(() => {
        if (!didMountRef.current) return;

        resetForm();
        setTopMessage(null);
        setTopMessageType(null);
    }, [pathname, resetForm]);

    React.useEffect(() => {
        if (nameError) nameRef.current?.focus?.();
        else if (emailError) emailRef.current?.focus?.();
    }, [nameError, emailError]);

    async function onSubmit(e: React.FormEvent) {
        e.preventDefault();

        setTopMessage(null);
        setTopMessageType(null);
        setNameError(null);
        setEmailError(null);
        setTermsError(null);

        if (isSubmitting) return;

        if (!formTokenRef.current) {
            await fetchFormToken();
        }

        if (!formTokenRef.current) {
            setTopMessageType("error");
            setTopMessage(
                "Não foi possível preparar o formulário. Recarregue a página."
            );
            return;
        }

        const submittedName = name;
        const submittedEmail = email;
        const submittedTerms = terms;
        const submittedNewsletter = newsletter;

        try {
            setIsSubmitting(true);

            const fd = new FormData();

            fd.append("_wpcf7", formId);
            fd.append("_wpcf7_locale", getCF7Locale(langCode));
            fd.append("_wpcf7_unit_tag", `wpcf7-f${formId}-o1`);
            fd.append("_wpcf7_container_post", "0");
            fd.append("_wpcf7_posted_data_hash", "");

            fd.append("your-name", submittedName);
            fd.append("your-email", submittedEmail);
            fd.append("lang", langCode);

            fd.append("terms-consent", submittedTerms ? "1" : "");
            fd.append("newsletter-consent", submittedNewsletter ? "1" : "");

            fd.append("website", website);
            fd.append("_form_token", formTokenRef.current);

            const res = await fetch(apiPath("/api/contact"), {
                method: "POST",
                body: fd,
            });

            const result: CF7Response = await res.json();

            if (result?.status === "mail_sent") {
                if (submittedNewsletter && submittedEmail) {
                    void fetch(apiPath("/api/egoi/subscribe"), {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                            email: submittedEmail,
                            name: submittedName,
                            lang: langCode,
                        }),
                    }).catch(() => { });
                }

                resetForm();
                setTopMessageType("success");
                setTopMessage(result.message || "Obrigado pela sua subscrição.");
                return;
            }

            if (result?.message) {
                setTopMessageType("error");
                setTopMessage(result.message);
            }

            if (
                result?.status === "validation_failed" &&
                Array.isArray(result.invalid_fields)
            ) {
                for (const errItem of result.invalid_fields) {
                    if (errItem.field === "your-name") setNameError(errItem.message);
                    if (errItem.field === "your-email") setEmailError(errItem.message);
                    if (errItem.field === "terms-consent") {
                        setTermsError(errItem.message);
                    }
                }
            }

            void fetchFormToken();
        } catch {
            setTopMessageType("error");
            setTopMessage("Não foi possível enviar o formulário.");
            void fetchFormToken();
        } finally {
            setIsSubmitting(false);
        }
    }

    const nameLabel = copy?.name_label?.trim() ?? "";
    const emailLabel = copy?.email_label?.trim() ?? "";
    const indicatesRequired = copy?.indicates_required?.trim() ?? "";
    const termsLegal = copy?.terms_legal?.trim() ?? "";
    const newsletterLegal = copy?.newsletter_legal?.trim() ?? "";
    const submitText = copy?.submit?.trim() ?? "";
    const honeypotLabel = copy?.honeypot?.trim() ?? "Website";
    return (
        <form
            ref={formRef}
            onSubmit={onSubmit}
            noValidate
            className="flex w-full max-w-full flex-col gap-8"
            suppressHydrationWarning
        >
            <div className="grid w-full grid-cols-1 gap-8 md:grid-cols-2">
                <div className="w-full min-w-0">
                    <Input
                        id="footer-name"
                        ref={nameRef}
                        label={nameLabel ? <SafeHtml content={nameLabel} /> : undefined}
                        type="text"
                        value={name}
                        onChange={(e) => {
                            setNameError(null);
                            setName(e.target.value);
                        }}
                        autoComplete="name"
                        aria-invalid={!!nameError || undefined}
                        aria-describedby={nameError ? "footer-name-error" : undefined}
                        invalid={!!nameError}
                    />

                    {nameError && (
                        <small
                            id="footer-name-error"
                            role="alert"
                            className="mt-1 block text-xs text-red-600"
                        >
                            {nameError}
                        </small>
                    )}
                </div>

                <div className="w-full min-w-0">
                    <Input
                        id="footer-email"
                        ref={emailRef}
                        label={emailLabel ? <SafeHtml content={emailLabel} /> : undefined}
                        type="email"
                        value={email}
                        onChange={(e) => {
                            setEmailError(null);
                            setEmail(e.target.value);
                        }}
                        autoComplete="email"
                        aria-invalid={!!emailError || undefined}
                        aria-describedby={emailError ? "footer-email-error" : undefined}
                        invalid={!!emailError}
                    />

                    {emailError && (
                        <small
                            id="footer-email-error"
                            role="alert"
                            className="mt-1 block text-body-3 text-red-600"
                        >
                            {emailError}
                        </small>
                    )}
                </div>
            </div>

            <div className="flex flex-col gap-4">
                {termsLegal && (
                    <div className="w-full min-w-0">
                        <Checkbox
                            id="footer-terms"
                            name="terms-consent"
                            checked={terms}
                            onChange={(e) => {
                                setTermsError(null);
                                setTerms(e.target.checked);
                            }}
                            label={<SafeHtml content={termsLegal} />}
                        />

                        {termsError && (
                            <small
                                id="footer-terms-error"
                                role="alert"
                                className="mt-1 block text-body-3 text-red-600"
                            >
                                {termsError}
                            </small>
                        )}
                    </div>
                )}

                {newsletterLegal && (
                    <div className="w-full min-w-0">
                        <Checkbox
                            id="footer-newsletter"
                            name="newsletter"
                            checked={newsletter}
                            onChange={(e) => setNewsletter(e.target.checked)}
                            label={<SafeHtml content={newsletterLegal} />}
                        />
                    </div>
                )}
            </div>

            <div
                aria-hidden="true"
                style={{
                    position: "absolute",
                    left: "-9999px",
                    width: "1px",
                    height: "1px",
                    overflow: "hidden",
                }}
            >
                <label htmlFor="footer-website">{honeypotLabel}</label>
                <input
                    id="footer-website"
                    name="website"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                />
            </div>

            {topMessage && (
                <p
                    aria-live="polite"
                    className={`m-0 text-sm ${topMessageType === "success" ? "font-bold" : "text-red-600"
                        }`}
                >
                    {topMessage}
                </p>
            )}

            <div className="flex w-full flex-wrap justify-between gap-2.5 max-sm:flex-col sm:items-end">
                <Button
                    type="submit"
                    className="group"
                    disabled={isSubmitting}
                    loading={isSubmitting}
                >
                    {submitText && <SafeHtml content={submitText} />}
                </Button>

                {indicatesRequired && (
                    <span className="block text-body-3 sm:self-end">
                        <SafeHtml content={indicatesRequired} />
                    </span>
                )}
            </div>
        </form>
    );
}